/**
 * CprViewport2D - Curved Planar Reformation viewport.
 *
 * Renders a straightened or stretched CPR view along an active centerline using
 * vtkImageCPRMapper. The mode is determined by the viewport type:
 *   - ViewportType.CPR_STRAIGHTENED → useStraightenedMode()
 *   - ViewportType.CPR_STRETCHED    → useStretchedMode()
 *
 * Public API:
 *   setAngle(degrees)   — rotate the sampling plane around the centerline tangent
 *   setDistance(mm)     — seek to a position along the centerline (null = midpoint)
 */

import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import { radiansFromDegrees } from '@kitware/vtk.js/Common/Core/Math';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkAbstractImageMapper from '@kitware/vtk.js/Rendering/Core/AbstractImageMapper';
import vtkImageCPRMapper from '@kitware/vtk.js/Rendering/Core/ImageCPRMapper';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import { mat3, mat4, vec3 } from 'gl-matrix';

import { BaseViewport } from './base-viewport';
import { ViewportType, type ViewportOptions, type ViewportVtkContext } from './types';
import type { ServiceContext } from '../core/types';
import type { Centerline } from '../domain/centerline';
import type { Volume } from '../domain/volume';
import { CprAngleService } from '../services/cpr-angle-service';
import { CurrentLocationService, type CurrentLocation } from '../services/current-location-service';
import { ViewportDataService } from '../services/viewport-data-service';
import { WindowLevelService, type WindowLevel } from '../services/window-level-service';
import { toVtkCPR } from '../utils/centerline-helper';

export class CprViewport2D extends BaseViewport {
    protected volume: Volume | null = null;
    protected centerline: Centerline | null = null;

    // CPR VTK pipeline
    private cprPolyData: vtkPolyData | null = null;
    private cprMapper: vtkImageCPRMapper | null = null;
    private imageSlice: vtkImageSlice | null = null;

    // CPR parameters
    private _angle: number = 0; // rotation angle in degrees around the centerline tangent
    private _distance: number | null = null; // distance along centerline in mm; null = use midpoint
    private _centerPoint: [number, number, number] | null = null; // world position used for stretched mode center

    constructor(
        id: string,
        type: ViewportType,
        vtkContext: ViewportVtkContext,
        context: ServiceContext,
        options?: ViewportOptions
    ) {
        super(id, type, vtkContext, context, options);
        this.setupOrthographicCamera();
        this.subscribeToVolumeData();
        this.subscribeToCenterlineData();
        this.subscribeToLocationService();
        this.subscribeToCprAngleService();
        this.subscribeToWindowLevelService();
    }

    // ========================================================================
    // Public CPR parameter API
    // ========================================================================

    /** Current rotation angle in degrees. */
    get angle(): number {
        return this._angle;
    }

    /** Current distance along the centerline in mm, or null when using the midpoint. */
    get distance(): number | null {
        return this._distance;
    }

    /**
     * Seek to a specific position along the centerline.
     * @param dist - Distance in mm from the start of the centerline, or null to use the midpoint
     */
    setDistance(dist: number | null): void {
        this._distance = dist;
        this.updateCPR();
        this.render();
    }

    // ========================================================================
    // Camera setup
    // ========================================================================

    private setupOrthographicCamera(): void {
        const camera = this.getRenderer(0).getActiveCamera();
        camera.setParallelProjection(true);
    }

    override updateCameraClippingRange(): void {
        this.getRenderer(0).resetCameraClippingRange();
    }

    // ========================================================================
    // CPR rendering pipeline
    // ========================================================================

    private createCPRPipeline(volume: Volume): void {
        this.disposeCPRPipeline();

        // Polydata container for the centerline — populated on each updateCPR()
        const cprPolyData = vtkPolyData.newInstance();
        this.cprPolyData = cprPolyData;

        const mapper = vtkImageCPRMapper.newInstance();
        mapper.setInputData(volume.imageData);
        mapper.setCenterlineData(cprPolyData);

        // Width must be large enough to cover any cross-section of the volume
        const dims = volume.imageData.getDimensions();
        const spacing = volume.imageData.getSpacing();
        const dimsVec = vec3.fromValues(dims[0], dims[1], dims[2]);
        const spacingVec = vec3.fromValues(spacing[0], spacing[1], spacing[2]);
        const diagonal = vec3.mul(vec3.create(), dimsVec, spacingVec);
        mapper.setWidth(2 * vec3.len(diagonal));

        const imageSlice = vtkImageSlice.newInstance();
        imageSlice.setMapper(mapper as unknown as vtkAbstractImageMapper);

        // Apply current W/L from service
        const wlService = this.context.services.getService<WindowLevelService>(
            WindowLevelService.SERVICE_NAME
        );
        this.applyWindowLevel(wlService.getWindowLevel());

        // Ensure proper interpolation mode (nearest neighbor by default)
        const property = imageSlice.getProperty();
        property.setInterpolationTypeToLinear();

        this.cprMapper = mapper;
        this.imageSlice = imageSlice;

        this.getRenderer(0).addActor(imageSlice);
    }

    /**
     * Recompute the CPR view — centerline polydata, mapper direction matrix,
     * actor transform and camera — using the current angle / distance / mode.
     *
     * Safe to call at any time; returns early when required data is missing.
     */
    private updateCPR(): void {
        if (!this.cprMapper || !this.cprPolyData || !this.volume || !this.centerline) return;

        const mapper = this.cprMapper;
        const renderer = this.getRenderer(0);

        // ---- 1. Feed centerline geometry into the mapper polydata ----
        const cprData = toVtkCPR(this.centerline);

        const centerlinePoints = Float32Array.from(cprData.position);
        const nPoints = centerlinePoints.length / 3;

        this.cprPolyData.getPoints().setData(centerlinePoints, 3);

        const centerlineLines = new Uint16Array(1 + nPoints);
        centerlineLines[0] = nPoints;
        for (let i = 0; i < nPoints; ++i) {
            centerlineLines[i + 1] = i;
        }
        this.cprPolyData.getLines().setData(centerlineLines);

        this.cprPolyData.getPointData().setTensors(
            vtkDataArray.newInstance({
                name: 'Orientation',
                numberOfComponents: 4,
                values: Float32Array.from(cprData.orientation),
            })
        );
        this.cprPolyData.modified();

        // ---- 2. Configure straightened vs stretched mode ----
        if (this.type === ViewportType.CPR_STRETCHED) {
            const defaultCenter: [number, number, number] = [
                centerlinePoints[0],
                centerlinePoints[1],
                centerlinePoints[2],
            ];
            mapper.useStretchedMode(this._centerPoint ?? defaultCenter);
        } else {
            mapper.useStraightenedMode();
        }

        // ---- 3. Resolve distance along the centerline ----
        const height: number = mapper.getHeight();
        const currentDistance = this._distance !== null ? this._distance : height / 2;

        // ---- 4. Query orientation at the target position ----
        const { position, orientation } = mapper.getCenterlinePositionAndOrientation(currentDistance) as {
            position: number[];
            orientation: number[]; // quaternion [x,y,z,w]
        };

        // ---- 5. Build world-space direction vectors ----
        const radAngle = radiansFromDegrees(this._angle);

        // Base directions in local frame
        const origin = vec3.fromValues(0, 0, 0);
        const normalDir = vec3.fromValues(0, 0, 1);
        const bitangentDir = vec3.fromValues(0, 1, 0);

        // Rotate bitangent around local Z (tangent) by the camera angle
        vec3.rotateZ(bitangentDir, bitangentDir, origin, radAngle);

        // Rotation matrix from centerline orientation quaternion
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const modelDirections = mat3.fromQuat(mat3.create(), orientation as any);

        // Transform local directions to world space
        const worldBitangent = vec3.transformMat3(vec3.create(), bitangentDir, modelDirections);
        const worldNormal = vec3.transformMat3(vec3.create(), normalDir, modelDirections);
        const worldTangent = vec3.normalize(
            vec3.create(),
            vec3.cross(vec3.create(), worldBitangent, worldNormal)
        );

        // ---- 6. Update the mapper direction matrix ----
        // baseDirections = inverseModelDirections * worldDirections
        const inverseModelDirections = mat3.invert(mat3.create(), modelDirections);
        const worldDirections = mat3.fromValues(
            worldTangent[0],
            worldTangent[1],
            worldTangent[2],
            worldBitangent[0],
            worldBitangent[1],
            worldBitangent[2],
            worldNormal[0],
            worldNormal[1],
            worldNormal[2]
        );
        const baseDirections = mat3.mul(mat3.create(), inverseModelDirections, worldDirections);
        mapper.setDirectionMatrix(baseDirections);

        // ---- 7. Build actor world transform ----
        // The actor quad origin sits at the bottom-left corner of the CPR image:
        //   X axis → worldTangent  (across the width)
        //   Y axis → worldNormal   (along the centerline height)
        //   Z axis → -worldBitangent (depth, pointing into the screen)
        const width: number = mapper.getWidth();
        const worldWidgetCenter = this._centerPoint
            ? vec3.fromValues(this._centerPoint[0], this._centerPoint[1], this._centerPoint[2])
            : vec3.fromValues(position[0], position[1], position[2]);

        const worldActorTranslation = vec3.scaleAndAdd(
            vec3.create(),
            worldWidgetCenter,
            worldTangent,
            -0.5 * width
        );
        vec3.scaleAndAdd(worldActorTranslation, worldActorTranslation, worldNormal, currentDistance - height);

        const negBitangent = vec3.scale(vec3.create(), worldBitangent, -1);
        const worldActorTransform = mat4.fromValues(
            worldTangent[0],
            worldTangent[1],
            worldTangent[2],
            0,
            worldNormal[0],
            worldNormal[1],
            worldNormal[2],
            0,
            negBitangent[0],
            negBitangent[1],
            negBitangent[2],
            0,
            worldActorTranslation[0],
            worldActorTranslation[1],
            worldActorTranslation[2],
            1
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.imageSlice as any).setUserMatrix(worldActorTransform);

        // ---- 8. Align camera to the CPR plane ----
        const camera = renderer.getActiveCamera();

        // Compute camera pull-back distance from the focal plane using the default view angle
        const cameraDistance = (0.5 * height) / Math.tan(radiansFromDegrees(0.5 * camera.getViewAngle()));

        camera.setParallelScale(0.5 * height);
        camera.setParallelProjection(true);

        // Focal point is the centre of the CPR image in world space
        const cameraFocalPoint = vec3.scaleAndAdd(
            vec3.create(),
            worldWidgetCenter,
            worldNormal,
            currentDistance - 0.5 * height
        );
        // Camera sits behind the focal point along the bitangent (screen depth) axis
        const cameraPosition = vec3.scaleAndAdd(
            vec3.create(),
            cameraFocalPoint,
            worldBitangent,
            -cameraDistance
        );

        camera.setPosition(cameraPosition[0], cameraPosition[1], cameraPosition[2]);
        camera.setFocalPoint(cameraFocalPoint[0], cameraFocalPoint[1], cameraFocalPoint[2]);
        camera.setViewUp(worldNormal[0], worldNormal[1], worldNormal[2]);

        renderer.resetCameraClippingRange();
    }

    private disposeCPRPipeline(): void {
        if (this.imageSlice) {
            this.getRenderer(0).removeActor(this.imageSlice);
        }
        this.imageSlice?.delete();
        this.cprMapper?.delete();
        this.cprPolyData?.delete();
        this.imageSlice = null;
        this.cprMapper = null;
        this.cprPolyData = null;
    }

    // ========================================================================
    // Data subscriptions
    // ========================================================================

    private subscribeToCprAngleService(): void {
        const cprAngleService = this.context.services.getService<CprAngleService>(
            CprAngleService.SERVICE_NAME
        );

        this._angle = cprAngleService.getCurrentAngle();

        this.unsubscribers.push(
            cprAngleService.subscribe<number>(cprAngleService.EVENTS.ANGLE_CHANGED, (degrees) => {
                this._angle = degrees;
                this.updateCPR();
                this.render();
            })
        );
    }

    private subscribeToVolumeData(): void {
        const dataService = this.context.services.getService<ViewportDataService>(
            ViewportDataService.SERVICE_NAME
        );

        const existing = dataService.getSlot('ct-volume');
        if (existing) {
            this.onVolumeChanged(existing);
        }

        this.unsubscribers.push(
            dataService.subscribeToSlot('ct-volume', (volume) => this.onVolumeChanged(volume))
        );
    }

    private subscribeToCenterlineData(): void {
        const dataService = this.context.services.getService<ViewportDataService>(
            ViewportDataService.SERVICE_NAME
        );

        const existing = dataService.getSlot('centerlines');
        if (existing) {
            this.onCenterlinesChanged(existing);
        }

        this.unsubscribers.push(
            dataService.subscribeToSlot('centerlines', (centerlines) =>
                this.onCenterlinesChanged(centerlines)
            )
        );
    }

    private subscribeToLocationService(): void {
        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );

        const existing = locationService.getLocation();
        if (existing) {
            this.onLocationChanged(existing);
        }

        this.unsubscribers.push(
            locationService.subscribe<CurrentLocation>(locationService.EVENTS.LOCATION_CHANGED, (location) =>
                this.onLocationChanged(location)
            )
        );

        this.unsubscribers.push(
            locationService.subscribe<Centerline | null>(
                locationService.EVENTS.ACTIVE_CENTERLINE_CHANGED,
                (centerline) => this.onActiveCenterlineChanged(centerline)
            )
        );
    }

    // ========================================================================
    // Event handlers
    // ========================================================================

    private onVolumeChanged(volume: Volume): void {
        this.volume = volume;
        this.createCPRPipeline(volume);
        this.updateCPR();
        this.render();
    }

    private onCenterlinesChanged(centerlines: Map<string, Centerline>): void {
        // Prefer the currently active centerline; fall back to the first available one
        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );
        const active = locationService.getActiveCenterline();

        if (active && centerlines.has(active.id)) {
            this.centerline = centerlines.get(active.id)!;
        } else if (centerlines.size > 0) {
            this.centerline = centerlines.values().next().value ?? null;
        } else {
            this.centerline = null;
        }

        this.updateCPR();
        this.render();
    }

    private onActiveCenterlineChanged(centerline: Centerline | null): void {
        this.centerline = centerline;
        this._distance = null; // Reset to midpoint when centerline switches
        this.updateCPR();
        this.render();
    }

    private onLocationChanged(location: CurrentLocation): void {
        this._centerPoint = location.worldPosition;

        // Sync the CPR position with the global location when it matches our centerline
        if (location.centerlinePosition) {
            const { centerline, nodeIndex } = location.centerlinePosition;
            if (!this.centerline || centerline.id === this.centerline.id) {
                this._distance = centerline.getDistanceAt(nodeIndex);
            }
        }

        this.updateCPR();
        this.render();
    }

    // ========================================================================
    // WindowLevelService subscription
    // ========================================================================

    private subscribeToWindowLevelService(): void {
        const wlService = this.context.services.getService<WindowLevelService>(
            WindowLevelService.SERVICE_NAME
        );

        this.applyWindowLevel(wlService.getWindowLevel());

        this.unsubscribers.push(
            wlService.subscribe<WindowLevel>(wlService.EVENTS.WINDOW_LEVEL_CHANGED, (wl) => {
                this.applyWindowLevel(wl);
                this.render();
            })
        );
    }

    private applyWindowLevel(wl: WindowLevel): void {
        if (!this.imageSlice) return;
        const property = this.imageSlice.getProperty();
        property.setColorWindow(wl.window);
        property.setColorLevel(wl.level);
    }

    // ========================================================================
    // Lifecycle
    // ========================================================================

    override dispose(): void {
        this.disposeCPRPipeline();
        super.dispose();
    }
}
